import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { HolidaySchema, IHolidaySettings } from "../api/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ApiResponse } from "common/models/ApiResponse";
import { showError, showSuccess } from "utils/showMessages";
import { showServerErrors } from "utils/formUtils";
import MainCard from "components/MainCard";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputLabel,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import RadioButtonGroup from "components/RadioButtonGroup";
import Loader from "components/Loader";
import { InfoCircleFilled } from "@ant-design/icons";
import { UpdateHolidaySettings } from "../api/mutation";
import { GetHolidaySettings } from "../api/hooks";

const HolidaySettingsTab = () => {
  const { libraryID } = useParams();
  const editMode = libraryID !== undefined;
  const queryClient = useQueryClient();

  const navigateTo = useNavigate();
  const updateMutation = UpdateHolidaySettings();

  const { data: getHolidaySettingsQuery, isFetching } = GetHolidaySettings(
    libraryID || ""
  );
  const [isSundayHolidayChecked, setIsSundayHolidayChecked] =
    useState<boolean>(false);
  const handleIsSundayHolidayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsSundayHolidayChecked(event.target.checked);
  };

  const [isSaturdayHolidayChecked, setIsSaturdayHolidayChecked] =
    useState<boolean>(false);
  const handleIsSaturdayHolidayChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsSaturdayHolidayChecked(event.target.checked);
  };

  const {
    control,
    handleSubmit,
    setError,
    register,
    reset,
    formState: { isSubmitting },
  } = useForm<IHolidaySettings>({
    resolver: zodResolver(HolidaySchema),
  });

  // const [holidaySettings, setHolidaySettings] = useState<IHolidaySettings>();
  useEffect(() => {
    if (getHolidaySettingsQuery) {
      // setHolidaySettings(getHolidaySettingsQuery.data.apiData);
      reset(getHolidaySettingsQuery?.apiData);
      setIsSaturdayHolidayChecked(
        getHolidaySettingsQuery?.apiData?.isSaturdayHoliday
      );
      setIsSundayHolidayChecked(
        getHolidaySettingsQuery?.apiData?.isSundayHoliday
      );
    }
  }, [getHolidaySettingsQuery, reset]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    data.libraryID = libraryID || "";

    updateMutation.mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  const handleResponse = async (response: ApiResponse<number> | undefined) => {
    if (response?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: ["library", "getHolidaySettings", "HolidaySettings"],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`../edit/${libraryID}/holiday`);
    } else {
      if (response?.status == 404) {
        showError(response?.title ?? "");
      } else if (response?.status == 400) {
        if (response?.modelErrors) {
          showServerErrors(setError, response.errors);
          showError(response?.title ?? "");
        } else {
          showError(response?.title ?? "");
        }
      } else {
        showError(response?.title ?? "");
      }
    }
  };

  return (
    <>
      <MainCard
        content={false}
        darkTitle
        darktitleVariant={"h4"}
        title={
          editMode
            ? "Holiday Settings for Issue-Return Circulation"
            : "Add Library"
        }
      >
        <Box component={"form"} sx={{ px: 2.5 }} onSubmit={onSubmit}>
          {isFetching ? <Loader /> : <></>}

          <Grid container spacing={2} direction={"column"}>
            <Grid item xs={12} md={6} lg={6} m={2}>
              <RadioButtonGroup
                control={control}
                label="Holiday Settings *"
                name="holidaySetting"
                options={[
                  {
                    id: "1",
                    label: "Do Not Consider Holiday",
                  },
                  {
                    id: "2",
                    label: "Increase Due Date By Holiday",
                  },
                  {
                    id: "3",
                    label: "Decrease Due Date By Holiday",
                  },
                ]}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} direction={"column"}>
            <Divider sx={{ py: 1 }} />
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              alignItems="center"
              spacing={1}
            >
              <Grid item xs={12} sm={10} md={8} lg={6}>
                <InputLabel htmlFor="isSaturdayHoliday">
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>
                      Do you want to consider saturday as a holiday?
                    </Stack>
                    <Stack>
                      <Tooltip
                        title="Increase Due Date by One Day (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
               <Grid
                item
                xs={12}
                sm={2}
                md={4}
                lg={6}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-start" },
                }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      {...register("isSaturdayHoliday")}
                      checked={isSaturdayHolidayChecked}
                      onChange={handleIsSaturdayHolidayChange}
                      id="isSaturdayHoliday"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label=""
                />
              </Grid>
            </Grid>
            <Divider sx={{ py: 1 }} />
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              alignItems="center"
              spacing={1}
            >
              <Grid item xs={12} sm={10} md={8} lg={6}>
                <InputLabel htmlFor="isSundayHoliday">
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Stack>Do you want to consider sunday as a holiday?</Stack>
                    <Stack>
                      <Tooltip
                        title="Increase Due Date by One Day (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                md={4}
                lg={6}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-start" },
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      {...register("isSundayHoliday")}
                      checked={isSundayHolidayChecked}
                      onChange={handleIsSundayHolidayCheckedChange}
                      id="isSundayHoliday"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label=""
                />
              </Grid>
            </Grid>
            <Divider sx={{ py: 1 }} />
          </Grid>

          <Grid container item spacing={2} mb={2} pt={2}>
            <Grid item sm={12}>
              <Button
                title={editMode ? "Update" : "Save"}
                type="submit"
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting && (
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    sx={{ mr: 1 }}
                  />
                )}
                {editMode ? "Update" : "Save"}
              </Button>
              <Button
                title="Back"
                sx={{ ml: 1 }}
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigateTo(`/admin/library`)}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>
    </>
  );
};
export default HolidaySettingsTab;
